/**
 * Role-based Access Control Utility
 * Provides granular permission checking for ERP modules
 */

// All available permissions organized by module
export const PERMISSIONS = {
  // HRMS
  'employees.view': 'View Employees',
  'employees.create': 'Create Employees',
  'employees.edit': 'Edit Employees',
  'employees.delete': 'Delete Employees',
  'attendance.view_own': 'View Own Attendance',
  'attendance.view_team': 'View Team Attendance',
  'attendance.view_all': 'View All Attendance',
  'attendance.checkin': 'Mark Attendance',
  'attendance.approve': 'Approve Regularization',
  'leaves.view_own': 'View Own Leaves',
  'leaves.view_team': 'View Team Leaves',
  'leaves.view_all': 'View All Leaves',
  'leaves.apply': 'Apply for Leave',
  'leaves.approve': 'Approve Leaves',
  'leaves.configure': 'Configure Leave Types',
  'payroll.view_own': 'View Own Payslips',
  'payroll.view_all': 'View All Payroll',
  'payroll.process': 'Process Payroll',
  'performance.view_own': 'View Own Performance',
  'performance.view_team': 'View Team Performance',
  'performance.conduct_review': 'Conduct Reviews',
  'expenses.view_own': 'View Own Expenses',
  'expenses.view_team': 'View Team Expenses',
  'expenses.submit': 'Submit Expenses',
  'expenses.approve': 'Approve Expenses',
  'hr.departments': 'Manage Departments',
  'hr.profiles': 'Manage Job Profiles',
  'hr.grades': 'Manage Grades',
  'hr.branches': 'Manage Branches',
  'hr.settings': 'HR Settings',
  // Finance
  'finance.dashboard': 'View Finance Dashboard',
  'finance.collections': 'Manage Collections',
  'accounting.vouchers': 'Create Vouchers',
  'accounting.ledgers': 'Manage Ledgers',
  // Inventory
  'procurement.suppliers': 'Manage Suppliers',
  'procurement.grn': 'Goods Receipt',
  'inventory.view': 'View Inventory',
  'inventory.manage': 'Manage Inventory',
  // TRC
  'trc.dashboard': 'TRC Dashboard',
  'trc.receiving': 'Receiving',
  'trc.grading': 'Grading',
  // Sales
  'sales.b2c': 'B2C Sales',
  'sales.b2b': 'B2B Sales',
  'sales.b2r': 'B2R Sales',
  // Franchisee
  'franchisee.view': 'View Franchisees',
  'franchisee.manage': 'Manage Franchisees',
  // Admin
  'admin.users': 'Manage Users',
  'admin.roles': 'Manage Roles',
  'admin.settings': 'System Settings',
};

// Module to permission mapping for navigation
export const MODULE_PERMISSIONS = {
  // Dashboard - accessible to all
  '/': [],
  '/dashboard': [],
  
  // HRMS Employee Management
  '/employees': ['employees.view'],
  '/add-employee': ['employees.create'],
  '/employee/*': ['employees.view'],
  
  // Attendance
  '/attendance': ['attendance.view_own', 'attendance.view_team', 'attendance.view_all'],
  '/offsite-attendance': ['attendance.checkin'],
  '/attendance-regularization': ['attendance.view_own', 'attendance.approve'],
  
  // Leaves
  '/leaves': ['leaves.view_own', 'leaves.view_team', 'leaves.view_all'],
  '/leave-config': ['leaves.configure'],
  '/leave-balances': ['leaves.view_all'],
  
  // Payroll
  '/payroll': ['payroll.view_own', 'payroll.view_all'],
  '/payroll-configuration': ['payroll.view_all'],
  '/salary-slips': ['payroll.view_own', 'payroll.view_all'],
  
  // Performance
  '/performance': ['performance.view_own', 'performance.view_team'],
  '/kpis': ['performance.conduct_review'],
  
  // Travel & Expense
  '/expenses': ['expenses.view_own', 'expenses.view_team'],
  '/travel-expense': ['expenses.submit'],
  
  // HR Admin
  '/hr-settings': ['hr.settings'],
  '/departments-config': ['hr.departments'],
  '/profiles-config': ['hr.profiles'],
  '/grades-config': ['hr.grades'],
  '/branch-config': ['hr.branches'],
  '/holidays': ['hr.settings'],
  
  // Finance
  '/finance-management': ['finance.dashboard', 'finance.collections'],
  '/accounting': ['accounting.vouchers', 'accounting.ledgers'],
  
  // Inventory
  '/suppliers': ['procurement.suppliers'],
  '/grn-list': ['procurement.grn'],
  '/inventory': ['inventory.view', 'inventory.manage'],
  
  // TRC
  '/trc/*': ['trc.dashboard', 'trc.receiving', 'trc.grading'],
  
  // Franchisee
  '/franchisee': ['franchisee.view', 'franchisee.manage'],
  
  // CRM & Customer 360
  '/crm': [],
  '/customer-360': [],
  '/my-account': [],
  '/customer-dashboard': [],
  '/call-center': [],
  
  // Buyback Management
  '/buyback-leads': [],
  '/sell-phone': [],
  '/buyback-sources': [],
  '/buyback-pricing': [],
  '/buyback-whatsapp': [],
  '/buyback-qc-config': [],
  
  // Admin
  '/role-management': ['admin.roles'],
  '/users-roles': ['admin.users'],
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  
  // Admin has all permissions
  if (userPermissions.includes('admin.*') || userPermissions.includes('*')) return true;
  
  return userPermissions.includes(requiredPermission);
};

/**
 * Check if user has any of the required permissions
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  
  // Admin has all permissions
  if (userPermissions.includes('admin.*') || userPermissions.includes('*')) return true;
  
  return requiredPermissions.some(perm => userPermissions.includes(perm));
};

/**
 * Check if user has access to a specific path
 */
export const hasAccessToPath = (userRole, userPermissions, path) => {
  // Handle null/undefined path
  if (!path) return true;
  
  // Admin has access to everything
  if (userRole === 'Admin') return true;
  
  // Find matching path pattern
  for (const [pattern, requiredPerms] of Object.entries(MODULE_PERMISSIONS)) {
    if (!pattern) continue;
    if (pattern === path || (pattern.endsWith('*') && path.startsWith(pattern.slice(0, -1)))) {
      if (requiredPerms.length === 0) return true;
      return hasAnyPermission(userPermissions, requiredPerms);
    }
  }
  
  // Default: allow access (for paths not in the list)
  return true;
};

/**
 * Get navigation items filtered by user permissions
 * @param {string} userRole - User's role name
 * @param {Array} fullNavigation - Full navigation array (or userPermissions for new API)
 * @param {Array} thirdParam - Optional: Full navigation if using new API
 */
export const getNavigationForRole = (userRole, secondParam, thirdParam) => {
  // Handle both old API (role, navigation) and new API (role, permissions, navigation)
  let userPermissions = [];
  let fullNavigation;
  
  if (thirdParam) {
    // New API: getNavigationForRole(role, permissions, navigation)
    userPermissions = secondParam || [];
    fullNavigation = thirdParam;
  } else {
    // Old API: getNavigationForRole(role, navigation)
    fullNavigation = secondParam;
  }
  
  // Admin sees everything
  if (userRole === 'Admin') return fullNavigation;
  
  // For Employee role with no specific permissions, use self-service nav
  if (userRole === 'Employee' && (!userPermissions || userPermissions.length === 0)) {
    return getEmployeeSelfServiceNav();
  }
  
  return fullNavigation.filter(item => {
    // Dashboard always visible
    if (item.path === '/' || item.path === '/dashboard') return true;
    
    // Check item permission
    if (item.path) {
      const hasAccess = hasAccessToPath(userRole, userPermissions, item.path);
      if (!hasAccess) return false;
    }
    
    // Filter sub-items if present
    if (item.subItems) {
      const filteredSubItems = item.subItems.filter(sub => 
        hasAccessToPath(userRole, userPermissions, sub.path)
      );
      
      // Only show parent if it has visible children
      if (filteredSubItems.length === 0) return false;
      
      return {
        ...item,
        subItems: filteredSubItems
      };
    }
    
    return true;
  }).map(item => {
    // Re-filter subItems for items that passed
    if (item.subItems) {
      return {
        ...item,
        subItems: item.subItems.filter(sub => 
          hasAccessToPath(userRole, userPermissions, sub.path)
        )
      };
    }
    return item;
  });
};

/**
 * Get employee self-service navigation
 */
export const getEmployeeSelfServiceNav = () => {
  return [
    { name: 'Dashboard', path: '/', icon: '📊' },
    { name: 'Approvals', path: '/approvals', icon: '✅' },
    {
      name: 'Productivity',
      icon: '⚡',
      subItems: [
        { name: 'Tasks', path: '/tasks', icon: '✅' },
      ]
    },
    {
      name: 'My HRMS',
      icon: '👤',
      subItems: [
        { name: 'My Attendance', path: '/attendance', icon: '📅' },
        { name: 'My Leaves', path: '/leaves', icon: '🌴' },
        { name: 'My Payslip', path: '/payroll', icon: '💰' },
        { name: 'Travel & Expenses', path: '/travel-expense', icon: '✈️' },
        { name: 'My Profile', path: '/profile', icon: '👤' }
      ]
    },
    {
      name: 'Resources',
      icon: '📚',
      subItems: [
        { name: 'Company Policies', path: '/policies', icon: '📜' },
        { name: 'Announcements', path: '/noticeboard', icon: '📌' },
      ]
    }
  ];
};

// Legacy role names for backward compatibility
export const ROLES = {
  ADMIN: 'Admin',
  HR: 'HR Manager',
  EMPLOYEE: 'Employee',
  MANAGER: 'Manager',
  BDM: 'BDM',
  ENGINEER: 'Engineer',
  FINANCE: 'Finance Manager',
  WAREHOUSE: 'Warehouse Manager',
  SALES: 'Sales Executive'
};
